// Loan and Negative Gearing Calculator Utilities

/**
 * Calculate loan repayments based on loan amount, interest rate, term, and payment frequency
 */
export const calculateLoanRepayment = (loanAmount, interestRate, loanTermYears, paymentFrequency = 'monthly', repaymentType = 'principalInterest') => {
  const principal = parseFloat(loanAmount);
  const annualRate = parseFloat(interestRate) / 100;
  const years = parseInt(loanTermYears);
  
  if (!principal || !annualRate || !years) {
    return null;
  }

  let paymentsPerYear;
  switch(paymentFrequency) {
    case 'weekly':
      paymentsPerYear = 52;
      break;
    case 'fortnightly':
      paymentsPerYear = 26;
      break;
    case 'monthly':
    default:
      paymentsPerYear = 12;
  }

  const periodicRate = annualRate / paymentsPerYear;
  const totalPayments = years * paymentsPerYear;

  let payment;
  if (repaymentType === 'interestOnly') {
    // Interest only payment
    payment = principal * periodicRate;
  } else {
    // Principal & Interest payment
    if (periodicRate === 0) {
      payment = principal / totalPayments;
    } else {
      payment = principal * (periodicRate * Math.pow(1 + periodicRate, totalPayments)) / 
                (Math.pow(1 + periodicRate, totalPayments) - 1);
    }
  }

  const totalRepayment = payment * totalPayments;
  const totalInterest = totalRepayment - principal;

  return {
    payment: payment.toFixed(2),
    totalRepayment: totalRepayment.toFixed(2),
    totalInterest: totalInterest.toFixed(2),
    paymentsPerYear,
    totalPayments,
    frequency: paymentFrequency
  };
};

/**
 * Compare repayments across all payment frequencies
 */
export const comparePaymentFrequencies = (loanAmount, interestRate, loanTermYears, repaymentType = 'principalInterest') => {
  const frequencies = ['monthly', 'fortnightly', 'weekly'];
  
  return frequencies.map(freq => {
    const result = calculateLoanRepayment(loanAmount, interestRate, loanTermYears, freq, repaymentType);
    return {
      frequency: freq,
      ...result
    };
  });
};

/**
 * Calculate negative gearing benefits for single or multiple properties
 */
export const calculateNegativeGearing = (properties, taxableIncome) => {
  if (!properties || properties.length === 0) {
    return null;
  }

  const income = parseFloat(taxableIncome);
  if (!income) return null;

  // Australian tax brackets 2024-25 (simplified)
  const getTaxRate = (income) => {
    if (income <= 18200) return 0;
    if (income <= 45000) return 0.19;
    if (income <= 120000) return 0.325;
    if (income <= 180000) return 0.37;
    return 0.45;
  };

  let totalAnnualRentalIncome = 0;
  let totalAnnualExpenses = 0;
  let totalLoanAmount = 0;

  const propertyResults = properties.map((property, index) => {
    const purchasePrice = parseFloat(property.purchasePrice) || 0;
    const weeklyRent = parseFloat(property.weeklyRent) || 0;
    const annualRentalIncome = weeklyRent * 52;
    
    const loanAmount = parseFloat(property.loanAmount) || purchasePrice * 0.8;
    const interestRate = parseFloat(property.interestRate) || 6;
    const annualInterest = (loanAmount * interestRate) / 100;
    
    const annualRates = parseFloat(property.annualRates) || purchasePrice * 0.01;
    const annualInsurance = parseFloat(property.annualInsurance) || 1500;
    const annualMaintenance = parseFloat(property.annualMaintenance) || annualRentalIncome * 0.05;
    const annualDepreciation = parseFloat(property.annualDepreciation) || purchasePrice * 0.025;
    
    const totalExpenses = annualInterest + annualRates + annualInsurance + annualMaintenance + annualDepreciation;
    const netRentalIncome = annualRentalIncome - totalExpenses;
    const cashFlowBeforeTax = annualRentalIncome - (totalExpenses - annualDepreciation); // Depreciation is non-cash
    
    totalAnnualRentalIncome += annualRentalIncome;
    totalAnnualExpenses += totalExpenses;
    totalLoanAmount += loanAmount;

    return {
      propertyNumber: index + 1,
      annualRentalIncome,
      totalExpenses,
      netRentalIncome,
      cashFlowBeforeTax,
      annualInterest,
      annualDepreciation
    };
  });

  // Calculate tax benefits
  const totalNetRentalIncome = totalAnnualRentalIncome - totalAnnualExpenses;
  const isNegativelyGeared = totalNetRentalIncome < 0;
  
  // Tax calculation
  const taxRateOriginal = getTaxRate(income);
  const taxRateWithProperty = getTaxRate(income + totalNetRentalIncome);
  
  const originalTax = calculateAustralianTax(income);
  const newTaxableIncome = income + totalNetRentalIncome;
  const newTax = calculateAustralianTax(newTaxableIncome);
  
  const taxSavings = originalTax - newTax;
  const totalCashFlow = (totalAnnualRentalIncome - (totalAnnualExpenses - properties.reduce((sum, p) => sum + (parseFloat(p.annualDepreciation) || 0), 0))) + taxSavings;

  return {
    properties: propertyResults,
    summary: {
      totalProperties: properties.length,
      totalAnnualRentalIncome,
      totalAnnualExpenses,
      totalNetRentalIncome,
      isNegativelyGeared,
      taxSavings: Math.max(taxSavings, 0),
      totalCashFlow,
      weeklyCashFlow: totalCashFlow / 52,
      originalTaxableIncome: income,
      newTaxableIncome: Math.max(newTaxableIncome, 0),
      totalLoanAmount
    }
  };
};

/**
 * Calculate Australian tax (simplified 2024-25)
 */
const calculateAustralianTax = (income) => {
  if (income <= 18200) return 0;
  if (income <= 45000) return (income - 18200) * 0.19;
  if (income <= 120000) return 5092 + (income - 45000) * 0.325;
  if (income <= 180000) return 29467 + (income - 120000) * 0.37;
  return 51667 + (income - 180000) * 0.45;
};

/**
 * Format currency
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Format currency with decimals
 */
export const formatCurrencyDecimal = (amount) => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};
